import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry from 'axios-retry';

import { getIdToken } from '@client/services/auth.service';
import { logger } from '@client/utils/logger.util';
import { getEnv } from '@common/utils';

const createAxiosInstance = (): AxiosInstance => {
  const baseURL = getEnv('VITE_API_BASE_URL', {
    env: import.meta.env,
    logger,
  }) as string;

  const instance = axios.create({
    baseURL,
    timeout: 30000,
  });

  // Configure retry behavior
  axiosRetry(instance, {
    retries: 3,
    retryDelay: (retryCount) => retryCount * 1000, // 1s, 2s, 3s
    retryCondition: (error) =>
      // Retry on network errors or 5xx status codes
      axiosRetry.isNetworkError(error) ||
      axiosRetry.isRetryableError(error) ||
      (error.response?.status >= 500 && error.response?.status < 600),
  });

  return instance;
};

const axiosInstance = createAxiosInstance();

axiosInstance.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const token = await getIdToken();

    if (token && config.headers) {
      config.headers.set?.('Authorization', `Bearer ${token}`);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Guard against the API gateway being unreachable: when `/api/v1/*` is not
// routed to the gateway (e.g. it falls through to the SPA host), the proxy
// answers 200 with the `index.html` body. Axios would then hand an HTML
// string to callers expecting JSON, surfacing as a cryptic `x.map is not a
// function` deep in render. Reject early with a clear, actionable message.
axiosInstance.interceptors.response.use(
  (response) => {
    const contentType = String(response.headers?.['content-type'] ?? '');

    if (
      typeof response.data === 'string' &&
      contentType.includes('text/html')
    ) {
      logger.error('API returned HTML instead of JSON', {
        url: response.config?.url,
        status: response.status,
      });

      return Promise.reject(
        new Error(
          `Expected JSON from ${response.config?.url ?? 'API'} but received ` +
            `HTML (status ${response.status}). The API gateway is likely ` +
            `unreachable or /api routing is misconfigured.`,
        ),
      );
    }

    return response;
  },
  (error) => Promise.reject(error),
);

export default axiosInstance;
